// @ts-ignore
import Chart, { ChartItem } from "chart.js/auto";
// @ts-ignore
import { ChartConfiguration } from "chart.js/dist/types";

type COLOR = "red" | "pink" | "brown" | "orange" | "yellow" | "dark-red" | "pink-tropical";
type SCHEME = "light" | "dark" | "tropical" | "tropical-dark";

interface SingleChartProps {
  color: COLOR;
  data: number[];
  title?: string;
}

export interface ChartDataProps extends SingleChartProps {
  title?: string;
  scheme: SCHEME;
  labels: string[];
  comparison_label?: string;
  comparison_data?: SingleChartProps[];
}

const COLORS: Record<COLOR, string> = {
  red: "#B0202895",
  pink: "#A1007D95",
  brown: "#50301895",
  orange: "#ED762395",
  yellow: "#FFCD0095",
  "dark-red": "#C5413895",
  "pink-tropical": "#E5007395",
};

const selectors: Record<
  "chart" | "chart_form" | "chart_form_label" | "chart_form_clear_button",
  string
> = {
  chart: "[data-chart]",
  chart_form: "[data-chart-form]",
  chart_form_label: "[data-chart-form-label]",
  chart_form_clear_button: "[data-chart-form-clear]",
};

const elements: Record<
  "chart" | "chart_form" | "chart_form_label" | "chart_form_clear_button",
  null | undefined | ChartItem | HTMLElement
> = {
  chart: null,
  chart_form: null,
  chart_form_label: null,
  chart_form_clear_button: null,
};

const comparison_status: Record<"active" | "disable", string> = {
  active: "active",
  disable: "disable",
};

const colorScheme: Record<SCHEME, any> = {
  light: {
    grid: "#FFFFFF33",
    angleLines: "#FFFFFF33",
    pointLabels: "#FFFFFF",
  },
  dark: {
    grid: "#00000033",
    angleLines: "#00000033",
    pointLabels: "#1A1919",
  },
  tropical: {
    grid: "#00000033",
    angleLines: "#00000033",
    pointLabels: "#1A1919",
  },
  "tropical-dark": {
    grid: "#FFFFFF33",
    angleLines: "#FFFFFF33",
    pointLabels: "#FFFFFF",
  },
};

function init() {
  elements.chart = document.querySelector(selectors.chart) as ChartItem;
  elements.chart_form = document.querySelector(selectors.chart_form) as HTMLElement;
  elements.chart_form_label = document.querySelector(selectors.chart_form_label) as HTMLElement;
  elements.chart_form_clear_button = document.querySelector(
    selectors.chart_form_clear_button
  ) as HTMLElement;
  const CHART_DATA: ChartDataProps = window.CHARD_DATA;

  if (!elements.chart || !CHART_DATA) return;

  function generateData(chart: SingleChartProps) {
    return {
      data: chart.data,
      fill: true,
      backgroundColor: COLORS[chart.color],
      borderColor: "transparent",
      pointBackgroundColor: "transparent",
      pointBorderColor: "transparent",
      pointHoverBackgroundColor: "transparent",
      pointHoverBorderColor: "transparent",
    };
  }

  const config: ChartConfiguration = {
    type: "radar",
    data: {
      labels: CHART_DATA.labels,
      datasets: [generateData(CHART_DATA)],
    },
    options: {
      responsive: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          // enabled: false,
        },
      },
      scales: {
        r: {
          suggestedMin: 0,
          suggestedMax:
            Math.max(
              ...CHART_DATA.data,
              ...(CHART_DATA.comparison_data?.map((cd) => Math.max(...cd.data)) || [])
            ) + 1,
          ticks: {
            display: false,
          },
          grid: {
            color: colorScheme[CHART_DATA.scheme].grid,
            lineWidth: 0.5,
          },
          angleLines: {
            color: colorScheme[CHART_DATA.scheme].angleLines,
            lineWidth: 0.5,
          },
          pointLabels: {
            color: colorScheme[CHART_DATA.scheme].pointLabels,
            font: {
              family: "Vanguard CF",
              size: "18px",
            },
          },
        },
      },
    },
  };

  // @ts-ignore
  const instance = new Chart(elements.chart, config);

  if (!elements.chart_form || !CHART_DATA.comparison_data) return;

  elements.chart_form.addEventListener("change", (e) => {
    const target = e.target as HTMLFormElement;
    // @ts-ignore
    elements.chart_form.classList.add(comparison_status.active);

    if (!elements.chart_form_label) return;

    // @ts-ignore
    elements.chart_form_label.innerText = target.value;

    const comparison_data: SingleChartProps | undefined = CHART_DATA.comparison_data?.find(
      (i) => i.title === target.value
    );

    if (comparison_data) {
      instance.data.datasets[1] = generateData(comparison_data);
      instance.update();
    }
  });

  if (!elements.chart_form_clear_button) return;

  const radios =
    Array.from(elements.chart_form.querySelectorAll("input")) || ([] as HTMLFormElement[]);

  elements.chart_form_clear_button.addEventListener("click", (e) => {
    e.stopPropagation();
    // @ts-ignore
    elements.chart_form.classList.remove(comparison_status.active);
    radios.map((el) => (el.checked = false));
    instance.data.datasets.pop();
    instance.update();
  });
}

document.addEventListener(
  "DOMContentLoaded",
  async function () {
    init();
  },
  {
    once: true,
  }
);
